import React from "react";
import { IExpandedCallSheetData } from "../../../../types/callsheet";
import type { IThemeState } from "../../../../types/theme";

interface PrintableEventDetailsProps {
  callSheetData?: IExpandedCallSheetData;
  eventDetails: {
    priest_name: string;
    eulogist_name: string;
    mc_name: string;
    other: string;
    program: string;
    notes: {
      travel: string;
      catering: string;
      logistics: string;
      technical: string;
      special_requirements: string;
      additional_notes: string;
    };
  };
  reduxTheme: IThemeState;
}

const PrintableEventDetails = React.forwardRef<
  HTMLDivElement,
  PrintableEventDetailsProps
>(({ eventDetails, reduxTheme, callSheetData }, ref) => {
  const printStyles = {
    container: {
      width: "100%",
      maxWidth: "210mm",
      margin: "0 auto",
      padding: "20mm",
      fontFamily: "Arial, sans-serif",
      fontSize: "12px",
      lineHeight: 1.5,
      color: "#000",
      backgroundColor: "#fff",
      minHeight: "297mm",
      pageBreakBefore: "always" as const,
      boxSizing: "border-box" as const,
    },
    header: {
      fontSize: "20px",
      fontWeight: "bold",
      marginBottom: "24px",
      borderBottom: "3px solid #333",
      paddingBottom: "12px",
      textAlign: "center" as const,
    },
    section: {
      marginBottom: "24px",
    },
    sectionTitle: {
      fontSize: "14px",
      fontWeight: "bold",
      marginBottom: "12px",
      backgroundColor: reduxTheme?.primary_colors?.[800] || "#333",
      color: reduxTheme?.primary_colors?.[100],
      padding: "8px 12px",
      border: "1px solid #ddd",
      borderRadius: "4px",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "20px",
      marginBottom: "24px",
    },
    programBox: {
      border: "2px solid #ddd",
      padding: "16px",
      minHeight: "320px",
      borderRadius: "6px",
      backgroundColor: "#fafafa",
    },
    infoBox: {
      border: "2px solid #ddd",
      padding: "16px",
      borderRadius: "6px",
      backgroundColor: "#fafafa",
    },
    infoItem: {
      marginBottom: "16px",
    },
    label: {
      fontWeight: "bold",
      fontSize: "12px",
      marginBottom: "6px",
      color: reduxTheme?.primary_colors?.[100],
    },
    value: {
      fontSize: "11px",
      minHeight: "24px",
      borderBottom: "1px solid #ccc",
      paddingBottom: "6px",
      paddingLeft: "4px",
    },
    notesGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "16px",
    },
    noteBox: {
      border: "2px solid #ddd",
      padding: "12px",
      marginBottom: "16px",
      borderRadius: "6px",
      backgroundColor: "#fafafa",
    },
    noteTitle: {
      fontSize: "12px",
      fontWeight: "bold",
      marginBottom: "8px",
      backgroundColor: reduxTheme?.primary_colors?.[800] || "#333",
      color: reduxTheme.primary_colors[100],
      padding: "6px 8px",
      margin: "-12px -12px 8px -12px",
      borderRadius: "4px 4px 0 0",
    },
    noteContent: {
      fontSize: "10px",
      minHeight: "80px",
      whiteSpace: "pre-wrap" as const,
      lineHeight: 1.4,
      padding: "4px",
    },
  };

  const notesSections = [
    { key: "travel", label: "Travel" },
    { key: "catering", label: "Catering" },
    { key: "logistics", label: "Logistics" },
    { key: "technical", label: "Technical" },
    { key: "special_requirements", label: "Special Requirements" },
    { key: "additional_notes", label: "Additional Notes" },
  ];

  return (
    <div ref={ref} style={printStyles.container}>
      {/* Header */}
      <div style={printStyles.header}>Event Details</div>

      {/* Main Content Grid */}
      <div style={printStyles.grid}>
        {/* Program Section */}
        <div style={printStyles.programBox}>
          <div style={printStyles.sectionTitle}>Program</div>
          <div
            style={{
              ...printStyles.noteContent,
              minHeight: "270px",
              fontSize: "11px",
            }}
          >
            {eventDetails.program || "No program details entered"}
          </div>
        </div>

        {/* Event Information */}
        <div style={printStyles.infoBox}>
          <div style={printStyles.sectionTitle}>Event Information</div>

          <div style={printStyles.infoItem}>
            <div style={printStyles.label}>Priest Name:</div>
            <div style={printStyles.value}>
              {callSheetData?.service?.preacher_name || "N/A"}
            </div>
          </div>

          <div style={printStyles.infoItem}>
            <div style={printStyles.label}>Eulogist Name:</div>
            <div style={printStyles.value}>
              {callSheetData?.service?.eulogist_name || "N/A"}
            </div>
          </div>

          <div style={printStyles.infoItem}>
            <div style={printStyles.label}>MC Name:</div>
            <div style={printStyles.value}>
              {callSheetData?.service?.mc_name || "N/A"}
            </div>
          </div>
        </div>
      </div>

      {/* Notes Sections */}
      <div style={printStyles.section}>
        <div style={printStyles.sectionTitle}>
          Notes & Additional Information
        </div>
        <div style={printStyles.notesGrid}>
          {notesSections.map((section) => (
            <div key={section.key} style={printStyles.noteBox}>
              <div style={printStyles.noteTitle}>{section.label}</div>
              <div style={printStyles.noteContent}>
                {eventDetails.notes[
                  section.key as keyof typeof eventDetails.notes
                ] || "No notes entered"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

PrintableEventDetails.displayName = "PrintableEventDetails";

export default PrintableEventDetails;
